import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths
} from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Sidebar } from "primereact/sidebar";
import { AuthContextType, useAuth } from "../../../auth/auth-provider";
import CalendarAppointment from "../CalendarAppointment";
import { CalendarEdit } from "../CalendarEdit";
import useCalendar, {
  CalendarAppointmentItem,
  GetInitialCalendarResponse,
  SearchCalendarAppointmentItem
} from "./use-calendar";
import {
  ActionButton,
  ActionsGroup,
  AgendaCheckbox,
  AgendaItem,
  AgendaList,
  AgendaName,
  AgendaTitle,
  CalendarCard,
  Container,
  ContentLayout,
  DayCell,
  DayGrid,
  DayNumber,
  DaySlotCell,
  DaysGrid,
  LoadingOverlay,
  MainColumn,
  MiniCalendarHeader,
  MiniDayButton,
  MiniDayDot,
  MiniDaysGrid,
  MiniNavButton,
  MiniNavGroup,
  MiniWeekLabel,
  MiniWeekRow,
  AppointmentTimeDot,
  MonthAppointment,
  MonthAppointments,
  MonthTitle,
  SearchInput,
  SearchInputIcon,
  SearchInputWrapper,
  SearchResultButton,
  SearchResultMeta,
  SearchResultsList,
  SearchResultsState,
  SearchResultTitle,
  SideCard,
  SideColumn,
  TimeGridScroller,
  TimeAppointment,
  TimeHeaderCell,
  TimeLabelCell,
  TimeSlotCell,
  TopBar,
  ViewDropdownWrapper,
  ViewMenu,
  ViewOption,
  ViewTrigger,
  WeekDayHeaderCell,
  WeekDayLabel,
  WeekDayNumber,
  WeekGrid,
  WeekHeaderCell,
  WeekHeaderRow
} from "./styles";

type ViewMode = "month" | "week" | "day";

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

const MONTH_BASE_DAY_HEIGHT = 110;
const MONTH_DAY_HEADER_HEIGHT = 44;
const MONTH_APPOINTMENT_ROW_HEIGHT = 38;
const HOUR_SLOT_HEIGHT = 56;
const MIN_APPOINTMENT_HEIGHT = 44;
const APPOINTMENT_GAP = 4;
const SLOT_PADDING = 4;

type PositionedAppointment = {
  appointment: CalendarAppointmentItem;
  topOffset: number;
  appointmentHeight: number;
  left: string;
  width: string;
};

const DEFAULT_FIRST_VISIBLE_HOUR = 8;
const DEFAULT_LAST_VISIBLE_HOUR = 18;

const getAppointmentStartHour = (appointment: CalendarAppointmentItem) =>
  new Date(appointment.startDate).getHours();

const getAppointmentEndHour = (appointment: CalendarAppointmentItem) => {
  const endDate = new Date(appointment.endDate);
  const endsOnExactHour =
    endDate.getMinutes() === 0 &&
    endDate.getSeconds() === 0 &&
    endDate.getMilliseconds() === 0;

  return endDate.getHours() + (endsOnExactHour ? 0 : 1);
};

const buildVisibleHourLabels = (appointments: CalendarAppointmentItem[]) => {
  const allHourLabels = Array.from({ length: 24 }, (_, hour) => `${hour}:00`);
  const firstVisibleHour = appointments.length
    ? Math.min(
        DEFAULT_FIRST_VISIBLE_HOUR,
        ...appointments.map(getAppointmentStartHour)
      )
    : DEFAULT_FIRST_VISIBLE_HOUR;
  const lastVisibleHour = appointments.length
    ? Math.max(
        DEFAULT_LAST_VISIBLE_HOUR,
        ...appointments.map(getAppointmentEndHour)
      )
    : DEFAULT_LAST_VISIBLE_HOUR;

  return allHourLabels.slice(firstVisibleHour, lastVisibleHour + 1);
};

export const CalendarPage: React.FC = () => {
  const { company, user, hasAnyPermission } = useAuth() as AuthContextType;
  const { getInitialCalendar, getCalendarView, searchCalendarAppointments } =
    useCalendar();
  const [viewMode, setViewMode] = useState<ViewMode>("month");
  const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));
  const [showViewMenu, setShowViewMenu] = useState(false);
  const [showSettingsView, setShowSettingsView] = useState(false);
  const [showCancelSettingsDialog, setShowCancelSettingsDialog] =
    useState(false);
  const [hasUnsavedCalendarEditChanges, setHasUnsavedCalendarEditChanges] =
    useState(false);
  const [showCreateTaskSidebar, setShowCreateTaskSidebar] = useState(false);
  const [appointmentSearchTerm, setAppointmentSearchTerm] = useState("");
  const [appointmentSearchResults, setAppointmentSearchResults] = useState<
    SearchCalendarAppointmentItem[]
  >([]);
  const [isAppointmentSearchLoading, setIsAppointmentSearchLoading] =
    useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<
    number | null
  >(null);
  const [draftAppointmentDate, setDraftAppointmentDate] = useState<Date | null>(
    null
  );
  const [initialCalendarData, setInitialCalendarData] =
    useState<GetInitialCalendarResponse | null>(null);
  const [ownerCalendarId, setOwnerCalendarId] = useState<number | null>(null);
  const [selectedCalendarId, setSelectedCalendarId] = useState<number | null>(
    null
  );
  const [isCalendarViewLoading, setIsCalendarViewLoading] = useState(false);
  const viewMenuRef = useRef<HTMLDivElement | null>(null);
  const fallbackTodayRef = useRef(startOfDay(new Date()));
  const hasLoadedInitialCalendarRef = useRef(false);
  const hasInitializedCalendarViewRef = useRef(false);
  const debouncedAppointmentSearchTerm = useDebounce(
    appointmentSearchTerm,
    600
  );

  const weekDays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const monthStart = startOfMonth(selectedDate);
  const monthEnd = endOfMonth(selectedDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

  const monthDays = useMemo(
    () =>
      eachDayOfInterval({
        start: calendarStart,
        end: calendarEnd
      }),
    [calendarStart, calendarEnd]
  );

  const weekDates = useMemo(() => {
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });

    return eachDayOfInterval({
      start: weekStart,
      end: endOfWeek(selectedDate, { weekStartsOn: 0 })
    });
  }, [selectedDate]);

  const miniCalendarMonthStart = startOfMonth(selectedDate);
  const miniCalendarMonthEnd = endOfMonth(selectedDate);
  const miniCalendarStart = startOfWeek(miniCalendarMonthStart, {
    weekStartsOn: 0
  });
  const miniCalendarEnd = endOfWeek(miniCalendarMonthEnd, {
    weekStartsOn: 0
  });
  const miniCalendarDays = eachDayOfInterval({
    start: miniCalendarStart,
    end: miniCalendarEnd
  });

  const visibleCalendars = useMemo(
    () =>
      [...(initialCalendarData?.visibleCalendars ?? [])]
        .sort((left, right) => {
          if (ownerCalendarId != null) {
            if (left.calendarId === ownerCalendarId) {
              return -1;
            }

            if (right.calendarId === ownerCalendarId) {
              return 1;
            }
          }

          return left.name.localeCompare(right.name);
        })
        .map((calendar) => ({
          ...calendar,
          checked:
            calendar.calendarId ===
            (selectedCalendarId ?? initialCalendarData?.calendar?.id)
        })),
    [initialCalendarData, ownerCalendarId, selectedCalendarId]
  );

  const appointments: CalendarAppointmentItem[] =
    initialCalendarData?.appointments ?? [];
  const currentMonth = selectedDate.getMonth() + 1;
  const currentYear = selectedDate.getFullYear();
  const weekAppointments = useMemo(
    () =>
      appointments.filter((appointment) =>
        weekDates.some((day) => isSameDay(new Date(appointment.startDate), day))
      ),
    [appointments, weekDates]
  );
  const dayAppointments = useMemo(
    () =>
      appointments.filter((appointment) =>
        isSameDay(new Date(appointment.startDate), selectedDate)
      ),
    [appointments, selectedDate]
  );
  const visibleHourLabels = useMemo(() => {
    if (viewMode === "day") {
      return buildVisibleHourLabels(dayAppointments);
    }

    if (viewMode === "week") {
      return buildVisibleHourLabels(weekAppointments);
    }

    return buildVisibleHourLabels(appointments);
  }, [appointments, dayAppointments, viewMode, weekAppointments]);
  const effectiveCompanyId = company?.id ?? user?.companyId;
  const activeCalendarId =
    selectedCalendarId ?? initialCalendarData?.calendar?.id ?? null;
  const primaryColor = user.plataform.mainColor || "#2624CD";
  const canManageAllCalendars =
    user.admin || hasAnyPermission("calendar_editAll");
  const activeVisibleCalendar =
    activeCalendarId == null
      ? null
      : (initialCalendarData?.visibleCalendars ?? []).find(
          (calendar) => calendar.calendarId === activeCalendarId
        ) ?? null;
  const canEditActiveCalendar =
    canManageAllCalendars ||
    Boolean(activeVisibleCalendar?.owner) ||
    (hasAnyPermission("calendar_edit") &&
      Boolean(activeVisibleCalendar?.sharedWithUser));
  const canShareActiveCalendar =
    canManageAllCalendars ||
    Boolean(activeVisibleCalendar?.owner) ||
    (hasAnyPermission("calendar_include") &&
      Boolean(activeVisibleCalendar?.sharedWithUser));
  const canManageActiveCalendar =
    canEditActiveCalendar || canShareActiveCalendar;

  const reloadCalendarView = useCallback(async () => {
    if (activeCalendarId == null) {
      return;
    }

    try {
      setIsCalendarViewLoading(true);
      const calendarViewResponse = await getCalendarView(
        activeCalendarId,
        currentMonth,
        currentYear
      );

      setInitialCalendarData((currentValue) =>
        currentValue
          ? {
              ...currentValue,
              calendar: calendarViewResponse.calendar,
              appointments: calendarViewResponse.appointments
            }
          : null
      );
    } catch (_error) {
      setInitialCalendarData((currentValue) =>
        currentValue
          ? {
              ...currentValue,
              appointments: []
            }
          : null
      );
    } finally {
      setIsCalendarViewLoading(false);
    }
  }, [activeCalendarId, currentMonth, currentYear, getCalendarView]);

  const handleCloseSettingsView = useCallback(() => {
    setHasUnsavedCalendarEditChanges(false);
    setShowCancelSettingsDialog(false);
    setShowSettingsView(false);
  }, []);

  const getAppointmentStartLabel = (startDate: string) =>
    format(new Date(startDate), "h:mm aa").toUpperCase();

  const getAppointmentsForDay = (day: Date) =>
    appointments.filter((appointment) =>
      isSameDay(new Date(appointment.startDate), day)
    );

  const getAppointmentsForDayAndHour = (day: Date, hourLabel: string) => {
    const hour = Number(hourLabel.split(":")[0]);

    return appointments.filter((appointment) => {
      const appointmentDate = new Date(appointment.startDate);

      return (
        isSameDay(appointmentDate, day) && appointmentDate.getHours() === hour
      );
    });
  };

  const getAppointmentDurationMinutes = (
    appointment: CalendarAppointmentItem
  ) => {
    const startDate = new Date(appointment.startDate);
    const endDate = new Date(appointment.endDate);

    return Math.max(0, (endDate.getTime() - startDate.getTime()) / 60000);
  };

  const getMonthDayMinHeight = (day: Date) =>
    Math.max(
      MONTH_BASE_DAY_HEIGHT,
      MONTH_DAY_HEADER_HEIGHT +
        getAppointmentsForDay(day).length * MONTH_APPOINTMENT_ROW_HEIGHT
    );

  const getWeekSlotLayout = (day: Date, hourLabel: string) => {
    const slotAppointments = getAppointmentsForDayAndHour(day, hourLabel).sort(
      (left, right) =>
        new Date(left.startDate).getTime() - new Date(right.startDate).getTime()
    );

    let rowHeight = HOUR_SLOT_HEIGHT;

    const layouts: PositionedAppointment[] = slotAppointments.map(
      (appointment, index) => {
        const topOffset =
          SLOT_PADDING + index * (MIN_APPOINTMENT_HEIGHT + APPOINTMENT_GAP);
        const appointmentHeight = MIN_APPOINTMENT_HEIGHT;

        rowHeight = Math.max(
          rowHeight,
          topOffset + appointmentHeight + SLOT_PADDING
        );

        return {
          appointment,
          topOffset,
          appointmentHeight,
          left: "4px",
          width: "calc(100% - 8px)"
        };
      }
    );

    return {
      layouts,
      rowHeight
    };
  };

  const weekSlotLayouts = useMemo(() => {
    const layoutMap = new Map<string, PositionedAppointment[]>();
    const rowHeightMap = new Map<string, number>();

    visibleHourLabels.forEach((timeLabel) => {
      let maxRowHeight = HOUR_SLOT_HEIGHT;

      weekDates.forEach((day) => {
        const key = `${day.toISOString()}-${timeLabel}`;
        const { layouts, rowHeight } = getWeekSlotLayout(day, timeLabel);

        layoutMap.set(key, layouts);
        maxRowHeight = Math.max(maxRowHeight, rowHeight);
      });

      rowHeightMap.set(timeLabel, maxRowHeight);
    });

    return {
      layoutMap,
      rowHeightMap
    };
  }, [appointments, visibleHourLabels, weekDates]);

  const daySlotLayouts = useMemo(() => {
    const layoutMap = new Map<string, PositionedAppointment[]>();
    const rowHeightMap = new Map<string, number>();
    visibleHourLabels.forEach((timeLabel) => {
      const slotAppointments = getAppointmentsForDayAndHour(
        selectedDate,
        timeLabel
      ).sort(
        (left, right) =>
          new Date(left.startDate).getTime() -
          new Date(right.startDate).getTime()
      );

      const layouts: PositionedAppointment[] = slotAppointments.map(
        (appointment, index) => {
          const startDate = new Date(appointment.startDate);
          const topOffset =
            SLOT_PADDING + (startDate.getMinutes() / 60) * HOUR_SLOT_HEIGHT;

          if (slotAppointments.length === 1) {
            return {
              appointment,
              topOffset,
              appointmentHeight: Math.max(
                MIN_APPOINTMENT_HEIGHT,
                (getAppointmentDurationMinutes(appointment) / 60) *
                  HOUR_SLOT_HEIGHT
              ),
              left: "4px",
              width: "calc(100% - 8px)"
            };
          }

          return {
            appointment,
            topOffset:
              topOffset + index * (MIN_APPOINTMENT_HEIGHT + APPOINTMENT_GAP),
            appointmentHeight: MIN_APPOINTMENT_HEIGHT,
            left: "4px",
            width: "calc(100% - 8px)"
          };
        }
      );

      layoutMap.set(timeLabel, layouts);

      const rowHeight =
        layouts.length <= 1
          ? HOUR_SLOT_HEIGHT
          : Math.max(
              HOUR_SLOT_HEIGHT,
              ...layouts.map(
                (layout) =>
                  layout.topOffset + layout.appointmentHeight + SLOT_PADDING
              )
            );

      rowHeightMap.set(timeLabel, rowHeight);
    });

    return {
      layoutMap,
      rowHeightMap
    };
  }, [appointments, selectedDate, visibleHourLabels]);

  useEffect(() => {
    let cancelled = false;

    const loadAppointmentSearchResults = async () => {
      const normalizedSearch = debouncedAppointmentSearchTerm.trim();

      if (activeCalendarId == null || normalizedSearch.length === 0) {
        setAppointmentSearchResults([]);
        setIsAppointmentSearchLoading(false);
        return;
      }

      try {
        setIsAppointmentSearchLoading(true);
        const response = await searchCalendarAppointments(
          activeCalendarId,
          normalizedSearch,
          10
        );

        if (cancelled) {
          return;
        }

        setAppointmentSearchResults(response.appointments);
      } catch (_error) {
        if (cancelled) {
          return;
        }

        setAppointmentSearchResults([]);
      } finally {
        if (!cancelled) {
          setIsAppointmentSearchLoading(false);
        }
      }
    };

    loadAppointmentSearchResults();

    return () => {
      cancelled = true;
    };
  }, [
    activeCalendarId,
    debouncedAppointmentSearchTerm,
    searchCalendarAppointments
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        viewMenuRef.current &&
        !viewMenuRef.current.contains(event.target as Node)
      ) {
        setShowViewMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const loadInitialCalendar = async () => {
      if (effectiveCompanyId == null || hasLoadedInitialCalendarRef.current) {
        return;
      }

      hasLoadedInitialCalendarRef.current = true;

      try {
        const response = await getInitialCalendar(effectiveCompanyId);

        if (!response) {
          setInitialCalendarData({
            currentMonth: fallbackTodayRef.current.getMonth() + 1,
            currentYear: fallbackTodayRef.current.getFullYear(),
            calendar: null,
            appointments: [],
            visibleCalendars: []
          });
          setOwnerCalendarId(null);
          return;
        }

        setInitialCalendarData(response);
        setOwnerCalendarId(
          (currentValue) =>
            currentValue ??
            response.visibleCalendars.find((calendar) => calendar.owner)
              ?.calendarId ??
            null
        );
        setSelectedCalendarId(response.calendar?.id ?? null);
      } catch (_error) {
        setInitialCalendarData({
          currentMonth: fallbackTodayRef.current.getMonth() + 1,
          currentYear: fallbackTodayRef.current.getFullYear(),
          calendar: null,
          appointments: [],
          visibleCalendars: []
        });
        setOwnerCalendarId(null);
      }
    };

    loadInitialCalendar();
  }, [effectiveCompanyId]);

  useEffect(() => {
    const loadCalendarView = async () => {
      if (selectedCalendarId == null) {
        return;
      }

      if (!hasInitializedCalendarViewRef.current) {
        hasInitializedCalendarViewRef.current = true;
        return;
      }

      await reloadCalendarView();
    };

    loadCalendarView();
  }, [selectedCalendarId, reloadCalendarView]);

  const renderMonthView = () => (
    <>
      <MonthTitle>
        {format(selectedDate, "MMMM yyyy", { locale: ptBR })}
      </MonthTitle>

      <WeekHeaderRow>
        {weekDays.map((weekDay) => (
          <WeekHeaderCell key={weekDay}>{weekDay}</WeekHeaderCell>
        ))}
      </WeekHeaderRow>

      <DaysGrid>
        {monthDays.map((day) => (
          <DayCell
            key={day.toISOString()}
            isCurrentMonth={isSameMonth(day, selectedDate)}
            minHeight={getMonthDayMinHeight(day)}
            onClick={() => {
              const now = new Date();
              const draftDate = new Date(day);
              draftDate.setHours(
                now.getHours(),
                now.getMinutes(),
                now.getSeconds(),
                now.getMilliseconds()
              );
              openCreateTaskSidebarAtDate(draftDate);
            }}
          >
            <DayNumber isToday={isToday(day)}>{format(day, "d")}</DayNumber>
            <MonthAppointments>
              {getAppointmentsForDay(day).map((appointment) => (
                <MonthAppointment
                  key={appointment.id}
                  onClick={(event) => {
                    event.stopPropagation();
                    openEditTaskSidebar(appointment.id);
                  }}
                >
                  {appointment.name}
                </MonthAppointment>
              ))}
            </MonthAppointments>
          </DayCell>
        ))}
      </DaysGrid>
    </>
  );

  const renderWeekView = () => (
    <>
      <MonthTitle>
        {format(weekDates[0], "dd", { locale: ptBR })} -{" "}
        {format(weekDates[6], "dd 'de' MMMM yyyy", { locale: ptBR })}
      </MonthTitle>

      <TimeGridScroller>
        <WeekGrid>
          <TimeHeaderCell />
          {weekDates.map((day) => (
            <WeekDayHeaderCell key={day.toISOString()}>
              <WeekDayLabel>
                {format(day, "EEE", { locale: ptBR })}
              </WeekDayLabel>
              <WeekDayNumber isToday={isToday(day)}>
                {format(day, "d")}
              </WeekDayNumber>
            </WeekDayHeaderCell>
          ))}

          {visibleHourLabels.flatMap((timeLabel) => [
            <TimeLabelCell
              key={`week-label-${timeLabel}`}
              rowHeight={weekSlotLayouts.rowHeightMap.get(timeLabel)}
            >
              {timeLabel}
            </TimeLabelCell>,
            ...weekDates.map((day) => (
              <TimeSlotCell
                key={`${day.toISOString()}-${timeLabel}`}
                rowHeight={weekSlotLayouts.rowHeightMap.get(timeLabel)}
                onClick={() => {
                  const [hour] = timeLabel.split(":").map(Number);
                  const draftDate = new Date(day);
                  draftDate.setHours(hour, 0, 0, 0);
                  openCreateTaskSidebarAtDate(draftDate);
                }}
              >
                {(
                  weekSlotLayouts.layoutMap.get(
                    `${day.toISOString()}-${timeLabel}`
                  ) ?? []
                ).map(
                  ({
                    appointment,
                    topOffset,
                    appointmentHeight,
                    left,
                    width
                  }) => (
                    <TimeAppointment
                      key={appointment.id}
                      onClick={(event) => {
                        event.stopPropagation();
                        openEditTaskSidebar(appointment.id);
                      }}
                      style={
                        {
                          "--appointment-top": `${topOffset}px`,
                          "--appointment-height": `${appointmentHeight}px`,
                          "--appointment-left": left,
                          "--appointment-width": width
                        } as CSSProperties
                      }
                    >
                      <strong>
                        {getAppointmentStartLabel(appointment.startDate)}
                        <AppointmentTimeDot />
                      </strong>
                      <span>{appointment.name}</span>
                    </TimeAppointment>
                  )
                )}
              </TimeSlotCell>
            ))
          ])}
        </WeekGrid>
      </TimeGridScroller>
    </>
  );

  const renderDayView = () => (
    <>
      <MonthTitle>
        {format(selectedDate, "dd 'de' MMMM yyyy", { locale: ptBR })}
      </MonthTitle>

      <TimeGridScroller>
        <DayGrid>
          <TimeHeaderCell />
          <WeekDayHeaderCell>
            <WeekDayLabel>
              {format(selectedDate, "EEE", { locale: ptBR })}
            </WeekDayLabel>
            <WeekDayNumber isToday={isToday(selectedDate)}>
              {format(selectedDate, "d")}
            </WeekDayNumber>
          </WeekDayHeaderCell>

          {visibleHourLabels.map((timeLabel) => [
            <TimeLabelCell
              key={`day-label-${timeLabel}`}
              rowHeight={daySlotLayouts.rowHeightMap.get(timeLabel)}
            >
              {timeLabel}
            </TimeLabelCell>,
            <DaySlotCell
              key={`day-slot-${timeLabel}`}
              rowHeight={daySlotLayouts.rowHeightMap.get(timeLabel)}
              onClick={() => {
                const [hour] = timeLabel.split(":").map(Number);
                const draftDate = new Date(selectedDate);
                draftDate.setHours(hour, 0, 0, 0);
                openCreateTaskSidebarAtDate(draftDate);
              }}
            >
              {(daySlotLayouts.layoutMap.get(timeLabel) ?? []).map(
                ({
                  appointment,
                  topOffset,
                  appointmentHeight,
                  left,
                  width
                }) => (
                  <TimeAppointment
                    key={appointment.id}
                    onClick={(event) => {
                      event.stopPropagation();
                      openEditTaskSidebar(appointment.id);
                    }}
                    style={
                      {
                        "--appointment-top": `${topOffset}px`,
                        "--appointment-height": `${appointmentHeight}px`,
                        "--appointment-left": left,
                        "--appointment-width": width
                      } as CSSProperties
                    }
                  >
                    <strong>
                      {getAppointmentStartLabel(appointment.startDate)}
                      <AppointmentTimeDot />
                    </strong>
                    <span>{appointment.name}</span>
                  </TimeAppointment>
                )
              )}
            </DaySlotCell>
          ])}
        </DayGrid>
      </TimeGridScroller>
    </>
  );

  const refreshCurrentCalendarView = async () => {
    if (selectedCalendarId == null) {
      return;
    }

    try {
      const calendarViewResponse = await getCalendarView(
        selectedCalendarId,
        currentMonth,
        currentYear
      );

      setInitialCalendarData((currentValue) =>
        currentValue
          ? {
              ...currentValue,
              calendar: calendarViewResponse.calendar,
              appointments: calendarViewResponse.appointments
            }
          : null
      );
    } catch (_error) {
      // no-op
    }
  };

  const openCreateTaskSidebar = () => {
    setSelectedAppointmentId(null);
    setDraftAppointmentDate(null);
    setShowCreateTaskSidebar(true);
  };

  const openCreateTaskSidebarAtDate = (date: Date) => {
    setSelectedAppointmentId(null);
    setDraftAppointmentDate(date);
    setShowCreateTaskSidebar(true);
  };

  const openEditTaskSidebar = (appointmentId: number) => {
    setSelectedAppointmentId(appointmentId);
    setShowCreateTaskSidebar(true);
  };

  const openSearchResultAppointment = (
    appointment: SearchCalendarAppointmentItem
  ) => {
    setSelectedDate(startOfDay(new Date(appointment.startDate)));
    setSelectedAppointmentId(appointment.id);
    setShowCreateTaskSidebar(true);
    setAppointmentSearchTerm("");
    setAppointmentSearchResults([]);
  };

  const renderSearchResults = () => {
    const normalizedSearch = debouncedAppointmentSearchTerm.trim();

    if (normalizedSearch.length === 0) {
      return null;
    }

    if (isAppointmentSearchLoading) {
      return <SearchResultsState>Buscando compromissos...</SearchResultsState>;
    }

    if (appointmentSearchResults.length === 0) {
      return (
        <SearchResultsState>Nenhum compromisso encontrado.</SearchResultsState>
      );
    }

    return (
      <SearchResultsList>
        {appointmentSearchResults.map((appointment) => {
          const metaParts = [
            format(new Date(appointment.startDate), "dd/MM/yyyy 'às' HH:mm"),
            appointment.contactName || appointment.contactPhone || null,
            appointment.description || null
          ].filter(Boolean);

          return (
            <SearchResultButton
              key={appointment.id}
              type="button"
              onClick={() => openSearchResultAppointment(appointment)}
            >
              <SearchResultTitle>{appointment.name}</SearchResultTitle>
              {metaParts.map((value, index) => (
                <SearchResultMeta key={`${appointment.id}-${index}`}>
                  {value}
                </SearchResultMeta>
              ))}
            </SearchResultButton>
          );
        })}
      </SearchResultsList>
    );
  };

  return (
    <Container>
      <Dialog
        visible={showCancelSettingsDialog}
        onHide={() => setShowCancelSettingsDialog(false)}
        draggable={false}
        header="Cancelar alterações"
        style={{ width: "575px" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            Você tem alterações que não foram salvas e ao voltar para o
            calendário você perderá essas alterações. Tem certeza que deseja
            continuar?
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "end",
              gap: "15px"
            }}
          >
            <Button
              label="Sim"
              className="p-button-danger"
              onClick={handleCloseSettingsView}
            />
            <Button
              label="Não"
              className="p-button-secondary"
              onClick={() => setShowCancelSettingsDialog(false)}
            />
          </div>
        </div>
      </Dialog>

      {isCalendarViewLoading ? (
        <LoadingOverlay>
          <ProgressSpinner />
        </LoadingOverlay>
      ) : null}
      <TopBar>
        {showSettingsView ? (
          <ViewTrigger
            onClick={() => {
              if (hasUnsavedCalendarEditChanges) {
                setShowCancelSettingsDialog(true);
                return;
              }

              handleCloseSettingsView();
            }}
            type="button"
          >
            <span>Calendário</span>
          </ViewTrigger>
        ) : (
          <>
            <ViewDropdownWrapper ref={viewMenuRef}>
              <ViewTrigger
                onClick={() => setShowViewMenu((previous) => !previous)}
                type="button"
              >
                <span>
                  {viewMode === "day"
                    ? "Dia"
                    : viewMode === "week"
                    ? "Semana"
                    : "Mês"}
                </span>
                <i className="pi pi-angle-down" />
              </ViewTrigger>
              {showViewMenu ? (
                <ViewMenu>
                  {[
                    { value: "day", label: "Dia" },
                    { value: "week", label: "Semana" },
                    { value: "month", label: "Mês" }
                  ].map((option) => (
                    <ViewOption
                      key={option.value}
                      isSelected={viewMode === option.value}
                      onClick={() => {
                        setViewMode(option.value as ViewMode);
                        setShowViewMenu(false);
                      }}
                      type="button"
                    >
                      {option.label}
                    </ViewOption>
                  ))}
                </ViewMenu>
              ) : null}
            </ViewDropdownWrapper>

            <ActionsGroup>
              {canManageActiveCalendar ? (
                <ActionButton
                  type="button"
                  variant="secondary"
                  color={primaryColor}
                  onClick={() => {
                    setShowSettingsView(true);
                    setShowViewMenu(false);
                  }}
                >
                  <i className="pi pi-cog" />
                  <span>Configurar</span>
                </ActionButton>
              ) : null}

              {canEditActiveCalendar ? (
                <ActionButton
                  type="button"
                  variant="primary"
                  color={primaryColor}
                  onClick={openCreateTaskSidebar}
                >
                  <i className="pi pi-plus-circle" />
                  <span>Criar Tarefa</span>
                </ActionButton>
              ) : null}
            </ActionsGroup>
          </>
        )}
      </TopBar>

      {showSettingsView ? (
        activeCalendarId != null && effectiveCompanyId != null ? (
          <CalendarEdit
            calendarId={activeCalendarId}
            companyId={effectiveCompanyId}
            canEditSettings={canEditActiveCalendar}
            canManageSharing={canShareActiveCalendar}
            onDirtyChange={setHasUnsavedCalendarEditChanges}
            onSaved={async () => {
              await reloadCalendarView();
              handleCloseSettingsView();
            }}
          />
        ) : null
      ) : (
        <ContentLayout>
          <SideColumn>
            <SideCard>
              <MiniCalendarHeader>
                {format(selectedDate, "MMMM yyyy", { locale: ptBR })}

                <MiniNavGroup>
                  <MiniNavButton
                    onClick={() =>
                      setSelectedDate((previous) => subMonths(previous, 1))
                    }
                    type="button"
                  >
                    ‹
                  </MiniNavButton>
                  <MiniNavButton
                    onClick={() =>
                      setSelectedDate((previous) => addMonths(previous, 1))
                    }
                    type="button"
                  >
                    ›
                  </MiniNavButton>
                </MiniNavGroup>
              </MiniCalendarHeader>

              <MiniWeekRow>
                {weekDays.map((weekDay) => (
                  <MiniWeekLabel key={weekDay}>{weekDay[0]}</MiniWeekLabel>
                ))}
              </MiniWeekRow>

              <MiniDaysGrid>
                {miniCalendarDays.map((day) => (
                  <MiniDayButton
                    key={day.toISOString()}
                    type="button"
                    isCurrentMonth={isSameMonth(day, selectedDate)}
                    isSelected={isSameDay(day, selectedDate)}
                    isToday={isToday(day)}
                    onClick={() => setSelectedDate(startOfDay(day))}
                  >
                    {format(day, "d")}
                    {getAppointmentsForDay(day).length > 0 ? (
                      <MiniDayDot />
                    ) : null}
                  </MiniDayButton>
                ))}
              </MiniDaysGrid>
            </SideCard>

            <SideCard>
              <SearchInputWrapper>
                <SearchInputIcon className="pi pi-search" />
                <SearchInput
                  type="text"
                  value={appointmentSearchTerm}
                  onChange={(event) =>
                    setAppointmentSearchTerm(event.target.value)
                  }
                  placeholder="Pesquisar"
                />
              </SearchInputWrapper>
              {renderSearchResults()}
            </SideCard>

            <SideCard>
              <AgendaTitle>Minhas agendas</AgendaTitle>

              <AgendaList compact={viewMode === "month"}>
                {visibleCalendars.map((calendar) => (
                  <AgendaItem
                    key={calendar.calendarId}
                    onClick={() => setSelectedCalendarId(calendar.calendarId)}
                  >
                    <AgendaCheckbox
                      type="checkbox"
                      checked={calendar.checked}
                      readOnly
                    />
                    <AgendaName title={calendar.name}>
                      {calendar.name}
                    </AgendaName>
                  </AgendaItem>
                ))}
              </AgendaList>
            </SideCard>
          </SideColumn>

          <MainColumn>
            <CalendarCard>
              {viewMode === "month" && renderMonthView()}
              {viewMode === "week" && renderWeekView()}
              {viewMode === "day" && renderDayView()}
            </CalendarCard>
          </MainColumn>
        </ContentLayout>
      )}

      <Sidebar
        visible={showCreateTaskSidebar}
        icons={selectedAppointmentId ? "Editar Tarefa" : "Criar Tarefa"}
        position="right"
        onHide={() => {
          setShowCreateTaskSidebar(false);
          setSelectedAppointmentId(null);
          setDraftAppointmentDate(null);
        }}
        style={{ width: "410px" }}
        className="wsDialog"
      >
        <CalendarAppointment
          appointmentId={selectedAppointmentId}
          currentCalendarId={
            selectedCalendarId ?? initialCalendarData?.calendar?.id ?? null
          }
          currentCalendarBreakAfterMinutes={
            initialCalendarData?.calendar?.breakAfterMinutes ?? null
          }
          existingAppointments={appointments}
          initialDate={draftAppointmentDate}
          canDeleteAppointment={canEditActiveCalendar}
          canSaveAppointment={canEditActiveCalendar}
          onClose={() => {
            setShowCreateTaskSidebar(false);
            setSelectedAppointmentId(null);
            setDraftAppointmentDate(null);
          }}
          onSuccess={async () => {
            await refreshCurrentCalendarView();
            setShowCreateTaskSidebar(false);
            setSelectedAppointmentId(null);
            setDraftAppointmentDate(null);
          }}
        />
      </Sidebar>
    </Container>
  );
};
